import React, { createContext, useContext, useEffect, useState } from 'react';
import { getOrgAppRoles } from 'src/services/apiService';
import { OrgAppRoles } from 'src/types';
import useAuth from './useAuth';

interface OrgAppRolesState {
  uid?: string | null;
  orgAppRoles: OrgAppRoles;
  filteredOrgAppRoles: OrgAppRoles;
  searchQuery: string;
  isLoading: number;
  error: string | null;
  fetchOrgAppRoles: (uid: string) => void;
  updateExpand: (expandAll: boolean, expand: boolean, key) => void;
  updateSearchQuery: (string) => void;
}

export const defaultOrgAppRolesContextValue = {
  uid: null,
  orgAppRoles: {
    appRoles: [],
  },
  filteredOrgAppRoles: {
    appRoles: [],
  },
  searchQuery: '',
  isLoading: 0,
  error: null,
  fetchOrgAppRoles: (_uid: string) => {
    throw new Error('Not Implemented');
  },
  updateExpand: (_expandAll: boolean, _exoand: boolean, _key) => {
    throw new Error('Not Implemented');
  },
  updateSearchQuery: () => {
    throw new Error('Not Implemented');
  },
};

const OrgAppRolesContext = createContext<OrgAppRolesState>(
  defaultOrgAppRolesContextValue
);

const useOrgAppRoles = (uid: string): OrgAppRolesState => {
  defaultOrgAppRolesContextValue.uid = uid;
  return useContext(OrgAppRolesContext);
};

export const OrgAppRolesProvider: React.FC = ({ children }) => {
  const {
    user: { id, organisationId },
  } = useAuth();

  const [orgAppRoles, setOrgAppRoles] = useState<OrgAppRoles>(
    defaultOrgAppRolesContextValue.orgAppRoles
  );

  const [filteredOrgAppRoles, setFilteredOrgAppRoles] = useState<OrgAppRoles>(
    defaultOrgAppRolesContextValue.orgAppRoles
  );

  const [isLoading, setIsLoading] = useState<number>(
    defaultOrgAppRolesContextValue.isLoading
  );

  const [error, setError] = useState(defaultOrgAppRolesContextValue.error);

  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchOrgAppRoles = async (uid: string) => {
    try {
      setIsLoading((prev) => prev + 1);
      setError(null);
      const data = await getOrgAppRoles(organisationId, uid || id);
      setOrgAppRoles({
        appRoles: data.appRoles
          .filter((item) => item.roles.length > 0)
          .map((item) => {
            return { ...item, expand: true };
          }),
      });
      setFilteredOrgAppRoles({
        appRoles: data.appRoles
          .filter((item) => item.roles.length > 0)
          .map((item) => {
            return { ...item, expand: true };
          }),
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading((prev) => prev - 1);
    }
  };

  const updateExpand = (expandAll: boolean, expand: boolean, key) => {
    if (expandAll) {
      setFilteredOrgAppRoles({
        appRoles: filteredOrgAppRoles.appRoles.map((item) => {
          return { ...item, expand: !expand };
        }),
      });
    } else {
      setFilteredOrgAppRoles({
        appRoles: filteredOrgAppRoles.appRoles.map((item, itemKey) => {
          if (itemKey === key) return { ...item, expand: !expand };

          return item;
        }),
      });
    }
  };

  const searchForPlatformAndRoles = (value) => {
    const filteredData = [];
    for (const item of orgAppRoles.appRoles) {
      if (item.platform.toLowerCase().includes(value.toLowerCase())) {
        filteredData.push(item);
      } else if (
        item.roles.find((roleItem) => {
          return roleItem.alias.toLowerCase().includes(value.toLowerCase());
        })
      ) {
        const filteredRoles = item.roles.filter((roleitem) =>
          roleitem.alias.toLowerCase().includes(value.toLowerCase())
        );
        filteredData.push({ ...item, roles: filteredRoles });
      }
    }
    return filteredData;
  };

  const updateSearchQuery = (value) => {
    setSearchQuery((prevState) => {
      if (prevState !== value) {
        setFilteredOrgAppRoles({
          appRoles:
            value.length === 0
              ? orgAppRoles.appRoles
              : searchForPlatformAndRoles(value),
        });
      }
      return value;
    });
  };

  useEffect(() => {
    fetchOrgAppRoles(defaultOrgAppRolesContextValue.uid);
    return () => {
      setOrgAppRoles(defaultOrgAppRolesContextValue.orgAppRoles);
      setFilteredOrgAppRoles(defaultOrgAppRolesContextValue.orgAppRoles);
      setIsLoading(defaultOrgAppRolesContextValue.isLoading);
      setError(defaultOrgAppRolesContextValue.error);
    };
  }, [organisationId]);

  return (
    <OrgAppRolesContext.Provider
      value={{
        orgAppRoles,
        filteredOrgAppRoles,
        searchQuery,
        isLoading,
        error,
        fetchOrgAppRoles,
        updateExpand,
        updateSearchQuery,
      }}
    >
      {children}
    </OrgAppRolesContext.Provider>
  );
};

export default useOrgAppRoles;
